import React from 'react';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { AxiosError } from 'axios';
import { SearchEntity } from 'v2/models';
import restFactory from 'v2/utils/restFactory';
import check from '../../icons/checkIconFilled.svg';
import sendPointingNorthEast from '../../icons/sendPointingNorthEast.svg';
import { toast } from 'react-toastify';
import styles from './submitToNomadButton.module.scss';
import { fetchAllProposals } from '../../../actions';
import { useMutation, useQueryClient } from 'react-query';
import { useFeatureFlagContext } from 'v2/context/featureFlagContext';
import { FEATURE_FLAGS } from 'utils/constants';
import { getKey as getUserSearchDetailsKey } from 'v2/queries/searches/useSearchDetails';
import { OverlayTrigger, Popover } from 'react-bootstrap';

interface SubmitToNomadButtonOwnProps {
  search: SearchEntity;
  disabled?: boolean;
  tooltipMessage?: string;
  onSubmit?: () => void;
  onError?: (error: string) => void;
  onComplete?: () => void;
  className?: string;
}

interface DispatchProps {
  // eslint-disable-next-line @typescript-eslint/ban-types
  fetchAllProposals: Function;
}

type SubmitToNomadButtonProps = DispatchProps & SubmitToNomadButtonOwnProps;

const SubmitToNomadButton = ({
  search,
  fetchAllProposals,
  disabled,
  tooltipMessage,
  onSubmit,
  onError,
  onComplete,
  className = 'btn-secondary',
}: SubmitToNomadButtonProps) => {
  const { flags } = useFeatureFlagContext();
  const isAutoAssignEnabled = flags && flags[FEATURE_FLAGS.REVIEWER_ASSIGNMENT];
  const queryClient = useQueryClient();
  const { mutate: submitSearch, isLoading } = useMutation(
    (data: SearchEntity) =>
      restFactory.post(
        `/dj/api/v1/search/${data.id}/submit_search_to_nomad/`,
        null
      ),
    {
      onMutate: async (data: SearchEntity) => {
        onSubmit?.();
        if (isAutoAssignEnabled) {
          await restFactory.get(
            `/dj/api/v1/search/${data.id}/assign_to_reviewer/`,
            { action: 'assign' }
          );
        }
      },
      onSuccess: async () => {
        toast.success('Request submitted to Nomad successfully');
      },
      onError: (error: AxiosError | string) => {
        const errorMessage =
          typeof error === 'string'
            ? error
            : 'An error ocurred while submitting the request to Nomad. Please contact support';
        toast.error(errorMessage);
        onError?.(errorMessage);
      },
      onSettled: async () => {
        await fetchAllProposals();
        onComplete?.();
        queryClient.invalidateQueries(getUserSearchDetailsKey(search.id));
      },
    }
  );

  return (
    <OverlayTrigger
      trigger={tooltipMessage ? ['click', 'focus', 'hover'] : []}
      overlay={
        <Popover id="submit-to-nomad" className={styles.submitDisabledPopover}>
          <Popover.Content className={styles.submitDisabledPopoverBody}>
            {tooltipMessage}
          </Popover.Content>
        </Popover>
      }
    >
      <button
        className={classnames(
          'btn',
          className,
          'text-nowrap',
          styles.submitToNomadButton
        )}
        title={'Submit to Nomad'}
        disabled={disabled || isLoading}
        onClick={() => submitSearch(search)}
      >
        <img
          src={className === 'btn-primary' ? sendPointingNorthEast : check}
          alt={'checkIcon'}
          className={'pr-2'}
        ></img>
        SUBMIT TO NOMAD
      </button>
    </OverlayTrigger>
  );
};

export default connect<null, DispatchProps>(null, {
  fetchAllProposals,
})(SubmitToNomadButton);
